import React from 'react';
import { API_URL } from '../../../../config';
import { DEFAULT_WORKFLOW_NAME } from '../constants';
import type { WorkflowAutomationStrategy } from '../../../../types/workflows';
import type {
  BrowserMode,
  BrowserEngine,
  BrowserViewportPreset,
  WorkflowInputDefinition,
  WorkflowScenario,
} from '@chatsdet/types';
import type { SidebarTab } from '../types';
import {
  WorkflowValidationError,
  type WorkflowValidationServerIssue,
} from '../../../../hooks/useWorkflowExecution';

type RunOptions = {
  triggerNameGeneration?: boolean;
  inputValues?: Record<string, string>;
  scenarioId?: string;
  environmentId?: string;
  environmentStageId?: string;
  viewportSize?: BrowserViewportPreset;
  browserEngine?: BrowserEngine;
  /** Superadmin-only: force the single-agent segment execution path for this run. */
  forceSingleAgentSegment?: boolean;
};

type UseRunExecutionParams = {
  workflowId: string | null;
  workflowName: string;
  nodes: any[];
  ensureWorkflow: () => Promise<string>;
  prepareRunConfig?: () => Promise<boolean | void>;
  beforeStartExecution?: () => Promise<void>;
  selectSidebarTab: (tab: SidebarTab) => void;
  startExecution: (opts: {
    automationStrategy: WorkflowAutomationStrategy;
    browserMode?: BrowserMode;
    viewportSize?: BrowserViewportPreset;
    browserEngine?: BrowserEngine;
    inputValues?: Record<string, string>;
    scenarioId?: string;
    environmentId?: string;
    environmentStageId?: string;
    forceSingleAgentSegment?: boolean;
  }) => Promise<any>;
  createWorkflowMutationIsPending: boolean;
  flowValidation: {
    setIssuesFromServer: (issues: WorkflowValidationServerIssue[]) => void;
    setIsPrevalidating: (v: boolean) => void;
    isPrevalidating: boolean;
  };
  setGeneratedName: (name: string | null) => void;
  setIsGeneratingName: (v: boolean) => void;
  workflowInputs?: WorkflowInputDefinition[];
  workflowScenarios?: WorkflowScenario[];
  eligibleEnvironmentIds?: string[];
  environments?: Array<{ id: string; name: string; type: string; isDefault: boolean }>;
};

export function useRunExecution({
  workflowId,
  workflowName,
  nodes,
  ensureWorkflow,
  prepareRunConfig,
  beforeStartExecution,
  selectSidebarTab,
  startExecution,
  createWorkflowMutationIsPending,
  flowValidation,
  setGeneratedName,
  setIsGeneratingName,
  workflowInputs,
  eligibleEnvironmentIds,
  environments,
}: UseRunExecutionParams) {
  const [selectedAutomationStrategy, setSelectedAutomationStrategy] =
    React.useState<WorkflowAutomationStrategy>('playwright');
  const [browserMode, setBrowserMode] = React.useState<BrowserMode>('headless');
  const canRunFlow = nodes.length > 0 && !createWorkflowMutationIsPending;
  const [isRunConfigModalOpen, setIsRunConfigModalOpen] = React.useState(false);
  const pendingRunOptionsRef = React.useRef<{ triggerNameGeneration?: boolean } | undefined>(
    undefined
  );

  const hasValueInputs = (workflowInputs?.length ?? 0) > 0;
  // Always offer "Run with config": every run can override browser engine and
  // viewport size, on top of any inputs/scenarios/environments the workflow defines.
  const hasRunConfig = true;

  const openRunConfigModal = React.useCallback(
    async (options?: { triggerNameGeneration?: boolean }) => {
      pendingRunOptionsRef.current = options;
      const prepared = await prepareRunConfig?.();
      if (prepared === false) {
        pendingRunOptionsRef.current = undefined;
        return;
      }
      setIsRunConfigModalOpen(true);
    },
    [prepareRunConfig]
  );

  const generateAndSetWorkflowName = React.useCallback(async () => {
    if (!workflowId || workflowName !== DEFAULT_WORKFLOW_NAME) {
      return;
    }

    setIsGeneratingName(true);

    try {
      const response = await fetch(`${API_URL}/workflows/${workflowId}/generate-name`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to generate workflow name');
      }

      const { name } = await response.json();
      setGeneratedName(name);
    } catch {
      // Silently fail - not critical for workflow execution
    } finally {
      setIsGeneratingName(false);
    }
  }, [workflowId, workflowName, setGeneratedName, setIsGeneratingName]);

  const executeRun = React.useCallback(
    async (options?: RunOptions) => {
      await ensureWorkflow();
      await beforeStartExecution?.();
      selectSidebarTab('run');
      if (options?.triggerNameGeneration) {
        void generateAndSetWorkflowName();
      }
      flowValidation.setIsPrevalidating(true);
      try {
        await startExecution({
          automationStrategy: selectedAutomationStrategy,
          browserMode: selectedAutomationStrategy === 'playwright' ? browserMode : undefined,
          viewportSize: options?.viewportSize,
          browserEngine: options?.browserEngine,
          inputValues: options?.inputValues,
          scenarioId: options?.scenarioId,
          environmentId: options?.environmentId,
          environmentStageId: options?.environmentStageId,
          forceSingleAgentSegment: options?.forceSingleAgentSegment,
        });
      } catch (error) {
        if (error instanceof WorkflowValidationError) {
          // Deterministic server-side validation rejected the run. Surface the
          // structured issues into the existing banner/dialog so node fields
          // get highlighted; abort the run cleanly.
          flowValidation.setIssuesFromServer(error.issues);
          return;
        }
        throw error;
      } finally {
        flowValidation.setIsPrevalidating(false);
      }
    },
    [
      ensureWorkflow,
      beforeStartExecution,
      selectSidebarTab,
      generateAndSetWorkflowName,
      startExecution,
      selectedAutomationStrategy,
      browserMode,
      flowValidation,
    ]
  );

  const handleRunFlow = React.useCallback(
    async (options?: { triggerNameGeneration?: boolean }) => {
      // No client-side pre-validation. The server is the gate; if validation
      // fails, executeRun's catch surfaces the issues into the banner.
      if (hasValueInputs) {
        await openRunConfigModal(options);
        return;
      }

      await executeRun(options);
    },
    [executeRun, hasValueInputs, openRunConfigModal]
  );

  const handleRunWithConfig = React.useCallback(
    async (options?: { triggerNameGeneration?: boolean }) => {
      await openRunConfigModal(options);
    },
    [openRunConfigModal]
  );

  const handleRunConfigConfirm = React.useCallback(
    async (args: {
      values?: Record<string, string>;
      scenarioId?: string;
      environmentId?: string;
      environmentStageId?: string;
      viewportSize?: BrowserViewportPreset;
      browserEngine?: BrowserEngine;
      forceSingleAgentSegment?: boolean;
    }) => {
      const options = pendingRunOptionsRef.current;
      pendingRunOptionsRef.current = undefined;
      await executeRun({
        ...options,
        inputValues: args.values,
        scenarioId: args.scenarioId,
        environmentId: args.environmentId,
        environmentStageId: args.environmentStageId,
        viewportSize: args.viewportSize,
        browserEngine: args.browserEngine,
        forceSingleAgentSegment: args.forceSingleAgentSegment,
      });
    },
    [executeRun]
  );

  return {
    selectedAutomationStrategy,
    setSelectedAutomationStrategy,
    browserMode,
    setBrowserMode,
    canRunFlow,
    generateAndSetWorkflowName,
    executeRun,
    handleRunFlow,
    handleRunWithConfig,
    hasRunConfig,
    isPrevalidating: flowValidation.isPrevalidating,
    isRunConfigModalOpen,
    setIsRunConfigModalOpen,
    handleRunConfigConfirm,
    eligibleEnvironmentIds,
    environments,
  };
}
